import Vue from 'vue';
import { mapMutations, mapState } from 'vuex';

import isSameMonth from 'date-fns/is_same_month';
import addDays from 'date-fns/add_days';
// import subDays from 'date-fns/sub_days';
import isBefore from 'date-fns/is_before';
import startOfMonth from 'date-fns/start_of_month';
import addMonths from 'date-fns/add_months';
import subMonths from 'date-fns/sub_months';
import isSameDay from 'date-fns/is_same_day';
import parse from 'date-fns/format';
import getMonth from 'date-fns/get_month';
import getDate from 'date-fns/get_date';
import getDay from 'date-fns/get_day';
import eachDay from 'date-fns/each_day';
import startOfWeek from 'date-fns/start_of_week';
import endOfWeek from 'date-fns/end_of_week';
import format from '../modules/formatDate';

import Calendar from '../classes/Calendar';

Vue.component('datepicker-component', {
    props: {
        startDate: {
            type: null,
            default() {
                return new Date().setHours(0, 0, 0, 0);
            },
        },
        maxMonths: null,
    },

    data() {
        return {
            today: new Date(),
            weekdays: [],
            calendarData: [],
            day: null,
            time: null,
            activeHour: null,
            hours: [],
            activeDay: null,
            loading: true,

            // Calendar
            showDatepicker: true,
            calendar: new Calendar(new Date()),
        };
    },

    computed: {
        ...mapState('reservation', {
            month: (state) => state.month,
            date: (state) => state.date,
            participants: (state) => state.participants,
            availableDates: (state) => state.availableDates,
        }),
    },

    mounted() {
        this.weekdays = this.setWeekdays();
        // this.initialMonthRender();
        // First fetch.
        this.getAvailability();
    },

    watch: {
        participants(value) {
            this.getAvailability();
        },
    },

    methods: {
        ...mapMutations('reservation', [
            'setMonth',
            'setDate',
        ]),

        setWeekdays() {
            // Construct weekdays.
            const now = new Date();
            const arr = eachDay(startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 }));
            const weekdays = [];

            for (let index = 0; index < arr.length; index++) {
                const d = arr[index];
                weekdays.push(format(d, 'dd'));
            }

            return weekdays;
        },

        /**
        * Fetches available times for the given day
        *
        * @param {object} dayObj
        */
        selectDay(dayObj) {
            // Show pop-up if today was selected.
            if (format(parse(dayObj.date), 'YYYY-MM-DD') === format(parse(this.today), 'YYYY-MM-DD')) {
                this.$modals.$emit('show-modal:booking-today');
                console.log('showing modal?');
                return;
            }

            // Do nothing if there's no availability.
            if (!dayObj.available) {
                return;
            }

            // Create an instance
            // Create an date-fns instance.
            // const day = parse(this.month).date(dayObj.d);
            const day = parse(dayObj.date);

            // If it's in the past, exit
            if (this.isBeforeStartDate(day)) {
                return;
            }

            if (this.activeDay) {
                this.activeDay.active = false;
            }

            this.activeDay = dayObj;
            this.activeDay.active = true;

            this.day = day;
            this.hours = [];

            this.time = null;
            this.setDate(dayObj.date);
            this.handleDateChange();
        },

        initialMonthRender() {
            this.data = [];

            const dates = [];
            this.days = [];
            // Get first day of month
            let day = startOfMonth(this.month);
            const currentMonth = this.month;
            const now = new Date();

            // Push a date while the day/month moment is current month
            do {
                // Create day moment
                this.days.push({
                    d: getDate(day),
                    past: isBefore(day, now),
                    today: isSameDay(now, day),
                    date: format(day, 'YYYY-MM-DD'),
                    available: dates.indexOf(format(day, 'YYYY-MM-DD')) > -1,
                    active: isSameDay(day, this.day),
                });

                // Move on to the next day.
                day = addDays(day, 1);
            } while (getMonth(day) === getMonth(currentMonth));

            this.render();
        },

        handleDateChange() {
            this.data = [];

            if (!this.dates) {
                return;
            }

            this.days = [];
            // Get first day of month
            let day = startOfMonth(this.month);
            const currentMonth = this.month;
            const now = new Date();

            // Push a date while the day/month moment is current month
            do {
                // Create day moment
                this.days.push({
                    d: getDate(day),
                    past: isBefore(day, now),
                    today: isSameDay(now, day),
                    date: format(day, 'YYYY-MM-DD'),
                    available: this.dates.indexOf(format(day, 'YYYY-MM-DD')) > -1,
                    active: isSameDay(day, this.day),
                });

                // Move on to the next day.
                day = addDays(day, 1);
            } while (getMonth(day) === getMonth(currentMonth));

            this.render();
        },

        /**
        * Fetch the availability data for a given month
        */
        async getAvailability() {
            this.loading = true;
            this.initialMonthRender();

            this.data = [];

            const { dates } = await this.$api.gms.reservation.availableDates(this.month, this.participants);
            this.dates = dates;
            this.days = [];
            // Get first day of month
            let day = startOfMonth(this.month);
            const currentMonth = this.month;
            const now = new Date();

            // Push a date while the day/month moment is current month
            do {
                // Create day moment
                this.days.push({
                    d: getDate(day),
                    past: isBefore(day, now),
                    today: isSameDay(now, day),
                    date: format(day, 'YYYY-MM-DD'),
                    available: dates.indexOf(format(day, 'YYYY-MM-DD')) > -1,
                    active: isSameDay(day, this.day),
                });

                // Move on to the next day.
                day = addDays(day, 1);
            } while (getMonth(day) === getMonth(currentMonth));

            this.loading = false;
            this.render();
        },

        /**
         * Navigate to the previous month
         */
        previousMonth() {
            const prev = subMonths(this.month, 1);
            this.setMonth(prev);

            this.getAvailability();
        },

        /**
         * Navigate to the next month
         */
        nextMonth() {
            const next = addMonths(this.month, 1);
            this.setMonth(next);

            this.getAvailability();
        },

        /**
         * Handle active day state
         *
         * @param  {Moment} dayObj
         * @return {Void}
         */
        handleActiveDay(dayObj) {
            if (this.activeDay) {
                this.activeDay.active = false;
            }

            this.activeDay = dayObj;
            this.activeDay.active = true;
        },

        /**
         * Render the show calendar
         */
        render() {
            const blocks = [];

            // Some padding at the beginning
            const start = getDay(startOfMonth(this.month));
            const max = (start === 0) ? 6 : start - 1;
            for (let p = 0; p < max; p++) {
                blocks.push({
                    d: null,
                });
            }

            // Merge in the array of days
            blocks.push(...this.days);

            // Some padding at the end if needed
            if (blocks.length % 7) {
                for (let p = (7 - (blocks.length % 7)); p > 0; p--) {
                    blocks.push({
                        d: null,
                    });
                }
            }

            // Split the array into "chunks" of seven
            const rows = blocks.map((e, i) => (i % 7 === 0 ? blocks.slice(i, i + 7) : null)).filter((e) => e);

            this.calendarData = rows;
        },

        inTodaysMonth() {
            return isSameMonth(this.month, this.today);
        },

        atMaxMonth() {
            const maxMonths = this.maxMonths ? this.maxMonths : 24;
            return isSameMonth(this.month, addMonths(this.today, maxMonths));
        },

        isBeforeStartDate(day) {
            return isBefore(day.date, this.startDate);
        },

        showMonth(month) {
            return format(month, 'MMMM YYYY');
        },
    },
});
